import { Fade, Paper } from '@mui/material';
import { PERMISSIONS } from '@vy/types';
import { validateEnum, withDefault } from '@vy/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { useTabParam } from '../../../hooks/use-tab-param';
import type { PageTab } from '../../../utils/types/page-tab';
import { EntitiesTabContent } from './settings-page-components/EntitiesTabContent/EntitiesTabContent';
import { GeneralTabContent } from './settings-page-components/GeneralTabContent/GeneralTabContent';
import { InvoicePreferenceTabContent } from './settings-page-components/InvoicePreferenceTabContent/InvoicePreferenceTabContent';
import { MembersTabContent } from './settings-page-components/MembersTabContent/MembersTabContent';
import type { SettingsPageTabOption } from './settings-page-types/settings-page-tab-option';
import { SETTINGS_PAGE_TAB_OPTIONS } from './settings-page-types/settings-page-tab-option';

const settingsPageTabs: Record<SettingsPageTabOption, PageTab<SettingsPageTabOption>> = {
  [SETTINGS_PAGE_TAB_OPTIONS.General]: {
    id: 0,
    render: <GeneralTabContent />,
    label: 'General',
    paramName: SETTINGS_PAGE_TAB_OPTIONS.General,
    permissions: [PERMISSIONS.ACCOUNT_CONFIGURE],
  },
  [SETTINGS_PAGE_TAB_OPTIONS.Members]: {
    id: 1,
    render: <MembersTabContent />,
    label: 'Members',
    paramName: SETTINGS_PAGE_TAB_OPTIONS.Members,
    permissions: [PERMISSIONS.USER_READ],
  },
  [SETTINGS_PAGE_TAB_OPTIONS.Entities]: {
    id: 2,
    render: <EntitiesTabContent />,
    label: 'Entities',
    paramName: SETTINGS_PAGE_TAB_OPTIONS.Entities,
  },
  [SETTINGS_PAGE_TAB_OPTIONS.Invoices]: {
    id: 3,
    render: <InvoicePreferenceTabContent />,
    label: 'Invoices Preferences',
    paramName: SETTINGS_PAGE_TAB_OPTIONS.Invoices,
  },
};

const SettingsPage: React.FC = () => {
  const [tab, setTab] = useTabParam<SettingsPageTabOption>(
    SETTINGS_PAGE_TAB_OPTIONS.General,
    deserializeSettingsPageTabParam,
  );
  const currentTab = withDefault(tab, SETTINGS_PAGE_TAB_OPTIONS.General);

  const tabs = Object.values(settingsPageTabs).map(({ label, paramName, permissions }) => ({
    label,
    value: paramName,
    permissions,
    onClick: () => setTab(paramName),
  }));

  return (
    <>
      <Helmet>
        <title>Settings</title>
      </Helmet>

      <VyLayout currentTab={currentTab} tabs={tabs}>
        {Object.entries(settingsPageTabs).map(([settingsPageTab, { id, render }]) => (
          <Fade key={id} hidden={currentTab !== settingsPageTab} in={currentTab === settingsPageTab}>
            <div className="w-full">
              <Paper>{render}</Paper>
            </div>
          </Fade>
        ))}
      </VyLayout>
    </>
  );
};

export const SettingsPageWithGlobalSearchParams = withGlobalSearchParams(SettingsPage);
export { SettingsPageWithGlobalSearchParams as SettingsPage };

function deserializeSettingsPageTabParam(tab: string): SettingsPageTabOption | null {
  validateEnum(tab, SETTINGS_PAGE_TAB_OPTIONS);

  return tab;
}
